/**
 * ContractBeslisKaart — een aflopend contract als besluit met knoppen.
 *
 * Bron: GET /api/workforce/contract-einde (de cron contract-einde legt de
 * beslispunten neer op T-60, T-35 en na de einddatum). Elke keuze gaat via
 * POST naar hetzelfde endpoint, dat het concept-contract maakt, de
 * aanzegmail stuurt (cc HR) en het besluit stempelt. "Nieuwe voorwaarden"
 * opent het contractformulier in modus verlenging (StaffDetailPage).
 *
 * Toont ook de uitkomst van een besluit, ook nadat de kaart zelf weg is
 * (de lijst houdt de uitkomsten vast), met de link naar werkgever-tekenen.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkforceStore } from '../../stores';
import type { ContractBeslispunt, ContractEindeKeuze } from '../../stores/workforce-store';

const MAANDEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

function datum(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()} ${MAANDEN[d.getMonth()]} ${d.getFullYear()}`;
}

function euro(v: number | string | null): string {
  if (v == null || v === '') return '';
  return `€ ${Number(v).toFixed(2).replace('.', ',')}`;
}

const VORM: Record<string, string> = {
  regulier: 'vaste uren',
  jaaruren: 'jaaruren',
  'vast-salaris': 'vast salaris',
  'min-max': 'min/max',
  '0-uren-allin': '0-uren all-in',
  '0-uren-opbouw': '0-uren met opbouw',
};

function voorwaarden(b: ContractBeslispunt): string {
  const c = b.contract;
  const delen = [VORM[c.employment_type] ?? c.employment_type];
  if (c.employment_type === 'min-max' && c.uren_min != null) delen.push(`${c.uren_min}-${c.uren_max ?? '?'} uur per week`);
  else if (c.uren_per_week != null) delen.push(`${c.uren_per_week} uur per week`);
  if (c.employment_type === 'vast-salaris' && c.bruto_maandsalaris != null) delen.push(`${euro(c.bruto_maandsalaris)} per maand`);
  else if (c.bruto_uurloon != null) delen.push(`${euro(c.bruto_uurloon)} per uur`);
  if (c.functie) delen.push(c.functie);
  return delen.join(', ');
}

function looptijdTekst(b: ContractBeslispunt): string {
  if (b.dagen > 0) return `loopt af op ${datum(b.einddatum)} (over ${b.dagen} ${b.dagen === 1 ? 'dag' : 'dagen'})`;
  if (b.dagen === 0) return `loopt vandaag af (${datum(b.einddatum)})`;
  return `liep af op ${datum(b.einddatum)}, ${-b.dagen} ${b.dagen === -1 ? 'dag' : 'dagen'} geleden`;
}

function ketenBadge(b: ContractBeslispunt): { tekst: string; kleur: string } | null {
  if (!b.keten) return null;
  if (b.keten.volgendeMoetOnbepaald) return { tekst: 'volgende is van rechtswege onbepaald', kleur: 'bg-red-100 text-red-700' };
  return { tekst: `${b.keten.aantal}e contract in de keten`, kleur: 'bg-slate-100 text-slate-600' };
}

function bevestigTekst(b: ContractBeslispunt, keuze: ContractEindeKeuze, mailAan: boolean): string {
  const mail = mailAan
    ? `${b.staff.name} krijgt de aanzegmail op ${b.staff.email ?? '(geen e-mailadres)'}, met HR in cc.`
    : 'De aanzegmail wordt in deze stand niet verstuurd.';
  const v = b.voorstel;
  const periode = !v ? '' : v.contract_type === 'onbepaald' || !v.end_date
    ? `voor onbepaalde tijd vanaf ${datum(v.start_date)}`
    : `van ${datum(v.start_date)} tot en met ${datum(v.end_date)}`;
  if (keuze === 'verlengen_zelfde') {
    return `Nieuw contract ${periode}, zelfde voorwaarden (${voorwaarden(b)}), geen proeftijd. Het komt als concept klaar; daarna teken je als werkgever en verstuur je het. ${mail}`;
  }
  if (keuze === 'niet_verlengen') {
    return `${b.staff.name} gaat uit dienst per ${datum(b.einddatum)}. De mail noemt de einddatum, de eindafrekening (vakantie-uren en vakantietoeslag via de laatste loonstrook) en het inleveren van spullen van de zaak. De dag na de einddatum gaan team-app en rooster vanzelf dicht. ${mail}`;
  }
  if (keuze === 'stilzwijgend_vastleggen') {
    return `${b.staff.name} werkte door na de einddatum, dus het contract is van rechtswege verlengd: zelfde voorwaarden, zelfde duur, hoogstens een jaar${periode ? ` (${periode})` : ''}. Er komt een getekende rij zonder handtekening; er gaat geen mail uit.`;
  }
  return '';
}

interface Uitkomst {
  beslispuntId: string;
  staffId: string;
  naam: string;
  tekst: string;
  nieuwContractId: string | null;
}

interface KaartProps {
  beslispunt: ContractBeslispunt;
  mailAan: boolean;
  onVerlengNieuw: (b: ContractBeslispunt) => void;
  onUitkomst: (u: Uitkomst) => void;
}

function Kaart({ beslispunt: b, mailAan, onVerlengNieuw, onUitkomst }: KaartProps) {
  const { besluitContractEinde } = useWorkforceStore();
  const [bevestig, setBevestig] = useState<ContractEindeKeuze | null>(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  const urgent = b.fase !== 't60';
  const badge = ketenBadge(b);

  const voerUit = async (keuze: ContractEindeKeuze) => {
    setBezig(true);
    setFout(null);
    const r = await besluitContractEinde(b.id, keuze);
    setBezig(false);
    if (!r.ok) {
      setFout(r.error ?? 'Besluit vastleggen mislukt');
      return;
    }
    const tekst = keuze === 'niet_verlengen'
      ? `${b.staff.name}: niet verlengd, uit dienst per ${datum(b.einddatum)}.`
      : keuze === 'stilzwijgend_vastleggen'
        ? `${b.staff.name}: stilzwijgende verlenging vastgelegd${r.nieuwContract ? ` (${r.nieuwContract.contract_number})` : ''}.`
        : `${b.staff.name}: verlengd, ${r.nieuwContract?.contract_number ?? 'nieuw contract'} staat als concept klaar.`;
    onUitkomst({
      beslispuntId: b.id,
      staffId: b.staff.id,
      naam: b.staff.name,
      tekst: tekst + (keuze !== 'stilzwijgend_vastleggen' ? (r.mailOnderdrukt ? ' Aanzegmail niet verstuurd (stand schaduw).' : ' Aanzegmail verstuurd.') : ''),
      nieuwContractId: keuze.startsWith('verlengen') ? (r.nieuwContract?.id ?? null) : null,
    });
  };

  return (
    <div className={`p-4 rounded-xl border ${urgent ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <span className={`text-sm font-semibold ${urgent ? 'text-red-900' : 'text-amber-900'}`}>
          {b.fase === 'verlopen' ? 'Contract verlopen, werkt door' : b.fase === 't35' ? 'Aanzegtermijn verloopt' : 'Contract loopt af'}: {b.staff.name}
        </span>
        {badge && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.kleur}`}>{badge.tekst}</span>}
      </div>
      <p className={`text-sm ${urgent ? 'text-red-800' : 'text-amber-800'}`}>
        {b.contract.nummer}, {voorwaarden(b)}, {b.contract.contract_type === 'bepaald' ? 'bepaalde tijd' : 'onbepaalde tijd'}
        {b.contract.stilzwijgend_verlengd ? ' (stilzwijgend verlengd)' : ''}, {looptijdTekst(b)}.
      </p>
      {b.fase === 'verlopen' ? (
        <p className="text-xs text-red-700 mt-1">
          Doorgewerkt na de einddatum zonder aanzegging: het contract is van rechtswege verlengd onder dezelfde voorwaarden. Leg dat vast, of verleng met een nieuw contract.
        </p>
      ) : b.aanzegplicht ? (
        <p className={`text-xs mt-1 ${urgent ? 'text-red-700' : 'text-amber-700'}`}>
          Aanzeggen uiterlijk {datum(b.aanzegUiterlijk)}.{b.fase === 't35' ? ' Daarna kost niet aanzeggen tot een maandsalaris (naar rato).' : ''}
        </p>
      ) : null}
      {b.keten?.volgendeMoetOnbepaald && b.keten.reden && (
        <p className="text-xs text-red-700 mt-1">Ketenregeling: {b.keten.reden}.</p>
      )}
      {!mailAan && (
        <p className="text-xs text-slate-500 mt-1">Stand schaduw: de aanzegmail wordt nu niet verstuurd; het besluit wordt wel vastgelegd.</p>
      )}

      {bevestig ? (
        <div className="mt-3 p-3 bg-white border border-slate-200 rounded-lg">
          <p className="text-sm text-slate-700 mb-3">{bevestigTekst(b, bevestig, mailAan)}</p>
          <div className="flex gap-2">
            <button
              onClick={() => voerUit(bevestig)}
              disabled={bezig}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              {bezig ? 'Vastleggen…' : 'Bevestigen'}
            </button>
            <button onClick={() => { setBevestig(null); setFout(null); }} disabled={bezig} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">
              Annuleren
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {b.toegestaneKeuzes.map(k => (
            <button
              key={k.keuze}
              onClick={() => (k.keuze === 'verlengen_nieuw' ? onVerlengNieuw(b) : setBevestig(k.keuze))}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                k.keuze === 'niet_verlengen'
                  ? 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {k.label}
            </button>
          ))}
        </div>
      )}
      {fout && <p className="mt-2 text-sm text-red-700">{fout}</p>}
    </div>
  );
}

interface ContractBeslisKaartenProps {
  beslispunten: ContractBeslispunt[];
  /** "Verlengen, nieuwe voorwaarden": het formulier openen (op de StaffDetailPage) */
  onVerlengNieuw?: (b: ContractBeslispunt) => void;
}

export function ContractBeslisKaarten({ beslispunten, onVerlengNieuw }: ContractBeslisKaartenProps) {
  const navigate = useNavigate();
  const { contractEindeModus } = useWorkforceStore();
  const [uitkomsten, setUitkomsten] = useState<Uitkomst[]>([]);
  const mailAan = contractEindeModus === 'live';

  if (beslispunten.length === 0 && uitkomsten.length === 0) return null;

  const naarNieuw = onVerlengNieuw ?? ((b: ContractBeslispunt) => navigate(`/personeel/${b.staff.id}?tab=contracten&verleng=${b.id}`));

  return (
    <div className="space-y-2 mb-4">
      {uitkomsten.map(u => (
        <div key={u.beslispuntId} className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-emerald-800">{u.tekst}</p>
          <div className="flex items-center gap-3">
            {u.nieuwContractId && (
              <button
                onClick={() => navigate(`/personeel/${u.staffId}?tab=contracten&contract=${u.nieuwContractId}`)}
                className="text-sm font-medium text-emerald-700 hover:text-emerald-900 underline"
              >
                Nu tekenen als werkgever
              </button>
            )}
            <button onClick={() => setUitkomsten(l => l.filter(x => x.beslispuntId !== u.beslispuntId))} className="text-xs text-emerald-600 hover:text-emerald-800">
              Sluiten
            </button>
          </div>
        </div>
      ))}
      {beslispunten.map(b => (
        <Kaart
          key={b.id}
          beslispunt={b}
          mailAan={mailAan}
          onVerlengNieuw={naarNieuw}
          onUitkomst={u => setUitkomsten(l => [...l, u])}
        />
      ))}
    </div>
  );
}
